import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  FormControlLabel,
  Switch,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MassageIcon from '@mui/icons-material/Spa';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useSessionsList from '../hooks/useSessionsList';
import LockIcon from '@mui/icons-material/Lock';
import SessionDetailsDialog from '../components/SessionDetailsDialog';

const ReviewTimelineScreen = () => {
  const adminUserLogin = useSelector((state) => state.adminUserLogin);
  const { adminUserInfo } = adminUserLogin;

  // Responsive logic for accordion default state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [surveyStatus, setSurveyStatus] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('-start_time');
  const [scoreEnabled, setScoreEnabled] = useState(true);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  // Accordion state - expanded by default on desktop, collapsed on mobile
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Update accordion state when media query changes
  useEffect(() => {
    setFiltersExpanded(!isMobile);
  }, [isMobile]);

  // Scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const {
    sessions,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSessionsList({
    token: adminUserInfo?.token,
    score: scoreEnabled,
    order_by: orderBy,
    page_size: pageSize,
    start_date: startDate ? startDate.toISOString().split('T')[0] : undefined,
    end_date: endDate ? endDate.toISOString().split('T')[0] : undefined,
    survey_status: surveyStatus,
  });

  // Handler functions for filters
  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
  };

  const handleEndDateChange = (newValue) => {
    setEndDate(newValue);
  };

  const handleSurveyStatusChange = (event) => {
    setSurveyStatus(event.target.value);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
  };

  const handleOrderByChange = (event) => {
    setOrderBy(event.target.value);
  };

  const handleScoreEnabledChange = (event) => {
    setScoreEnabled(event.target.checked);
  };

  // Accordion handler
  const handleFiltersToggle = (event, isExpanded) => {
    setFiltersExpanded(isExpanded);
  };

  // Intersection Observer for infinite scroll
  const observer = useRef();
  const lastSessionElementRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasNextPage, fetchNextPage, isFetchingNextPage],
  );

  const handleSessionClick = (sessionId) => {
    setSelectedSessionId(sessionId);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedSessionId(null);
  };

  // Format time function for the dialog
  const formatTime = (time) => {
    const result = parseISO(time, { timeZone: 'Toronto/Canada' });
    return format(result, 'PPp', { timeZone: 'Toronto/Canada' });
  };

  const getStatusColor = (surveyStatus) => {
    switch (surveyStatus) {
      case 0:
        return 'warning'; // Pending
      case 1:
        return 'success'; // Completed
      default:
        return 'default';
    }
  };

  const getStatusText = (surveyStatus) => {
    switch (surveyStatus) {
      case 0:
        return 'Pending Survey';
      case 1:
        return 'Survey Complete';
      default:
        return 'Unknown';
    }
  };

  // Skeleton Card Component
  const SkeletonCard = () => (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
      <Card
        elevation={0}
        sx={{
          height: '100%',
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          transition: 'all 0.2s ease-in-out',
          backgroundColor: '#f8fafc',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Header with locker number skeleton */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Skeleton variant="text" width={120} height={28} />
            <Skeleton variant="circular" width={40} height={40} />
          </Box>

          {/* Masseuses skeleton */}
          <Box display="flex" alignItems="center" mb={1}>
            <Skeleton
              variant="circular"
              width={16}
              height={16}
              sx={{ mr: 1 }}
            />
            <Skeleton variant="text" width={100} height={20} />
          </Box>

          {/* Session type skeleton */}
          <Box display="flex" alignItems="center" mb={1}>
            <Skeleton
              variant="circular"
              width={16}
              height={16}
              sx={{ mr: 1 }}
            />
            <Skeleton variant="text" width={90} height={20} />
          </Box>

          {/* Date and time skeleton */}
          <Skeleton variant="text" width={140} height={20} sx={{ mb: 2 }} />

          {/* Status and score skeleton */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Skeleton variant="rounded" width={80} height={24} />
            <Skeleton variant="rounded" width={60} height={24} />
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  if (isError) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">
          Error loading sessions: {error?.message || 'Unknown error'}
        </Alert>
      </Container>
    );
  }

  // Calculate review statistics
  const totalSessions = sessions.length;
  const reviewedSessions = sessions.filter(
    (session) => session.survey_status === 1,
  ).length;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography
        variant="h2"
        component="h1"
        gutterBottom
        mt={6}
        sx={{ mb: 2 }}
      >
        Session Review Timeline
      </Typography>

      {/* Review Opportunity Card */}
      <Card
        elevation={0}
        sx={{
          mb: 4,
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          backgroundColor: '#f8fafc',
          borderLeft: '4px solid #10B981',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}
              >
                Review Opportunity Taken
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#10B981',
                  fontFamily: 'monospace',
                }}
              >
                {reviewedSessions}/{totalSessions}
              </Typography>
              {totalSessions > 0 && (
                <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
                  {((reviewedSessions / totalSessions) * 100).toFixed(1)}%
                  completion rate found for the selected filters
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 60,
                height: 60,
                borderRadius: '50%',
                backgroundColor: '#10B981',
                color: 'white',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {totalSessions > 0
                  ? Math.round((reviewedSessions / totalSessions) * 100)
                  : 0}
                %
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filters Section */}
      <Accordion
        expanded={filtersExpanded}
        onChange={handleFiltersToggle}
        elevation={0}
        sx={{
          mb: 4,
          border: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '0 0 32px 0',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: '#f8fafc',
            borderRadius: '8px 8px 0 0',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
            },
          }}
        >
          <Box display="flex" alignItems="center">
            <FilterListIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
            <Typography
              variant="body2"
              sx={{ ml: 2, color: '#64748b', fontStyle: 'italic' }}
            >
              {isMobile ? 'Tap to expand' : 'Click to collapse'}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            p: 3,
            backgroundColor: '#f8fafc',
            borderRadius: '0 0 8px 8px',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Start Date */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  disableFuture
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* End Date */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Tooltip title={!startDate && 'Select start date first'}>
                <div>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={handleEndDateChange}
                      disableFuture
                      minDate={startDate}
                      disabled={!startDate}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </Tooltip>
            </Grid>

            {/* Survey Status Filter */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="survey-status-select-label">
                  Survey Status
                </InputLabel>
                <Select
                  labelId="survey-status-select-label"
                  value={surveyStatus}
                  label="Survey Status"
                  onChange={handleSurveyStatusChange}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="0">Not Reviewed</MenuItem>
                  <MenuItem value="1">Reviewed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Order By */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="order-select-label">Sort By</InputLabel>
                <Select
                  labelId="order-select-label"
                  value={orderBy}
                  label="Sort By"
                  onChange={handleOrderByChange}
                >
                  <MenuItem value="-start_time">Newest First</MenuItem>
                  <MenuItem value="start_time">Oldest First</MenuItem>
                  <MenuItem value="-score">Highest Score</MenuItem>
                  <MenuItem value="score">Lowest Score</MenuItem>
                  <MenuItem value="-period">Period (Desc)</MenuItem>
                  <MenuItem value="period">Period (Asc)</MenuItem>
                  <MenuItem value="-survey_status">
                    Survey Status (Desc)
                  </MenuItem>
                  <MenuItem value="survey_status">Survey Status (Asc)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Page Size */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="page-size-select-label">Per Page</InputLabel>
                <Select
                  labelId="page-size-select-label"
                  value={pageSize}
                  label="Per Page"
                  onChange={handlePageSizeChange}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={30}>30</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1000</MenuItem>
                  <MenuItem value={5000}>5000</MenuItem>
                  <MenuItem value={10000}>10000</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Score Enable Toggle */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={scoreEnabled}
                    onChange={handleScoreEnabledChange}
                    size="small"
                  />
                }
                label="Show Score"
                labelPlacement="start"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {isLoading && sessions.length === 0 ? (
        <Grid container spacing={3}>
          {Array.from({ length: 8 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </Grid>
      ) : (
        <Grid container spacing={3}>
          {sessions.map((session, index) => {
            const isLastElement = index === sessions.length - 1;

            return (
              <Grid
                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                key={session.id}
                ref={isLastElement ? lastSessionElementRef : null}
              >
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    backgroundColor: '#f8fafc',
                    borderLeft: `4px solid ${
                      session.session_type?.color || '#C38A2D'
                    }`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                  onClick={() => handleSessionClick(session.id)}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Header with locker number */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{ fontWeight: 600, color: '#1e293b' }}
                      >
                        {session.member?.name || session.customer}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 50,
                          height: 40,
                          borderRadius: '10%',
                          backgroundColor:
                            session.session_type?.color || '#C38A2D',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      >
                        <LockIcon />
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {session.locker}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Masseuses */}
                    <Box display="flex" alignItems="center" mb={1}>
                      <MassageIcon
                        sx={{ mr: 1, fontSize: 16, color: '#C38A2D' }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: '#64748b', fontWeight: 500 }}
                      >
                        {session.masseuses?.map((m) => m.name).join(', ')}
                      </Typography>
                    </Box>

                    {/* Session type and duration */}
                    <Box display="flex" alignItems="center" mb={1}>
                      <AccessTimeIcon
                        sx={{ mr: 1, fontSize: 16, color: '#A0721F' }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: '#64748b', fontWeight: 500 }}
                      >
                        {session.session_type?.name}
                      </Typography>
                    </Box>

                    {/* Date and time */}
                    <Typography
                      variant="body2"
                      sx={{ color: '#64748b', mb: 2, fontWeight: 500 }}
                    >
                      {format(
                        new Date(session.start_time),
                        'MMM dd, yyyy • h:mm a',
                      )}
                    </Typography>

                    {/* Status and score */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mt={2}
                    >
                      <Chip
                        label={getStatusText(session.survey_status)}
                        color={getStatusColor(session.survey_status)}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          '& .MuiChip-label': {
                            px: 2,
                          },
                        }}
                      />
                      {session.score !== null && (
                        <Chip
                          label={`Score: ${session.score}`}
                          variant="contained"
                          size="small"
                          sx={{
                            color: '#FFFFFF',
                            backgroundColor: 'primary.main',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: '#754c00ff',
                            },
                          }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Loading indicator for next page */}
      {isFetchingNextPage && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading more sessions...
          </Typography>
        </Box>
      )}

      {/* End of results indicator */}
      {!hasNextPage && sessions.length > 0 && (
        <Box display="flex" justifyContent="center" my={4}>
          <Typography variant="body2" color="text.secondary">
            No more sessions to load
          </Typography>
        </Box>
      )}

      {/* Session Details Dialog */}
      <SessionDetailsDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        sessionId={selectedSessionId}
        formatTime={formatTime}
        token={adminUserInfo?.token}
      />
    </Container>
  );
};

export default ReviewTimelineScreen;
